import {useMemo} from 'react';
import {FieldArrayRenderProps, useFormikContext} from 'formik';
import {t} from 'i18next';
import {useTranslation} from 'react-i18next';

import {BUTTON_THEME, BUTTON_VARIANT} from '@/constants/button';
import {DOMAIN_VALUE_ALLOCATION_GUARANTEED_FUNDS_ACCOUNTS} from '@/constants/engine';
import {useConfig} from '@/contexts/config';
import {SupportedLocale} from '@/initializers/i18next';
import {CustomValue} from '@/model/types/value';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import Button from '../button';
import {AllocationFormSchemaType} from './allocation-form-schema';
import {InvestmentAccountSubform} from './investment-account-subform';
import {ignoreValuesWithIdIn, keepOnlyActiveValues, mapValueToOptions, sortLabelAsc} from './utils';

const GUARANTEED_INVESTMENT_FUNDS_IDS_TO_IGNORE: string[] = []; // Add the IDs of the guaranteed investment funds (DV-95) to ignore

type Props = {
  arrayHelpers: FieldArrayRenderProps;
};

export const AllocationFormGuaranteedInvestmentFundsTable = ({arrayHelpers}: Props) => {
  const {i18n} = useTranslation();
  const config = useConfig();
  const {values} = useFormikContext<AllocationFormSchemaType>();

  const {title: accountTypeName, values: investmentAccounts} = useMemo(() => {
    const fundsConfig = config.model.values.find(
      value => value.id === DOMAIN_VALUE_ALLOCATION_GUARANTEED_FUNDS_ACCOUNTS,
    );
    if (!fundsConfig) return {title: '', values: []};

    return {
      title: fundsConfig.description[i18n.language as SupportedLocale],
      values: (fundsConfig.values as CustomValue[])
        .filter(ignoreValuesWithIdIn(GUARANTEED_INVESTMENT_FUNDS_IDS_TO_IGNORE))
        .filter(keepOnlyActiveValues)
        .map(mapValueToOptions(i18n.language as SupportedLocale))
        .sort(sortLabelAsc),
    };
  }, [config.model.values, i18n.language]);

  return (
    <div>
      <div className="mt-4 flex items-center justify-between rounded-t-md bg-violet px-4 py-3">
        <div className="font-bold text-white">{t('components.advisor-predetermined-allocation-form.table.title')}</div>

        <Button
          type="button"
          theme={BUTTON_THEME.TERTIARY}
          variant={BUTTON_VARIANT.SMALL}
          label={t('components.advisor-predetermined-allocation-form.table.add')}
          onClick={() => arrayHelpers.push({amount: '', accountId: ''})}
          trackingKey={WEB_CLICK_TRACKING_KEY.ADD_ALLOCATION_ACCOUNT}
        />
      </div>

      <div className="flex flex-col gap-4 rounded-b-md border border-grey-400 p-6 pt-8">
        {values.accounts.length ? (
          values.accounts.map((account, index) => (
            <InvestmentAccountSubform
              key={index}
              accountTypeName={accountTypeName}
              investmentAccounts={investmentAccounts}
              onRemove={() => arrayHelpers.remove(index)}
              account={account}
              index={index}
            />
          ))
        ) : (
          <div className="flex items-center justify-center pb-2">
            {t('components.advisor-predetermined-allocation-form.table.empty')}
          </div>
        )}
      </div>
    </div>
  );
};
